// src/app/(dashboard)/user-management/Filters.tsx
"use client";

import { FilterDrawerView } from "@core/components/controlled-table/table-filter";
import { useState } from "react";
import { useMedia } from "react-use";
import { PiFunnel, PiMagnifyingGlassBold } from "react-icons/pi";
import { Button, Flex, Input } from "rizzui";
import ToggleColumns from "@core/components/table-utils/toggle-columns";
import type { Table as ReactTableType } from "@tanstack/react-table";

interface TableToolbarProps<T extends Record<string, any>> {
  table: ReactTableType<T>;
}

export default function Filters<TData extends Record<string, any>>({
  table,
}: TableToolbarProps<TData>) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const isLarge = useMedia("(min-width: 1920px)", false);

  return (
    <Flex align="center" justify="between" className="mb-4 gap-0">
      <Flex align="center" className="w-auto flex-wrap">
        <Input
          type="search"
          placeholder="Search by User ID..."
          value={(table.getColumn("userId")?.getFilterValue() as string) ?? ""}
          onClear={() => table.getColumn("userId")?.setFilterValue(undefined)}
          onChange={(e) =>
            table.getColumn("userId")?.setFilterValue(e.target.value)
          }
          inputClassName="h-9"
          clearable
          prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
        />

        {isLarge && showFilters && <FilterElements table={table} />}
      </Flex>

      <Flex align="center" className="w-auto">
        <Button
          variant="outline"
          className="h-[34px] px-2"
          onClick={() =>
            isLarge ? setShowFilters((f) => !f) : setOpenDrawer((d) => !d)
          }
        >
          <PiFunnel className="me-1.5 h-4 w-4" />
          {isLarge && showFilters ? "Hide Filters" : "Filters"}
        </Button>

        {!isLarge && (
          <FilterDrawerView
            drawerTitle="Table Filters"
            isOpen={openDrawer}
            setOpenDrawer={setOpenDrawer}
          >
            <div className="grid grid-cols-1 gap-6">
              <FilterElements table={table} />
            </div>
          </FilterDrawerView>
        )}

        <ToggleColumns table={table} />
      </Flex>
    </Flex>
  );
}

function FilterElements<T extends Record<string, any>>({
  table,
}: TableToolbarProps<T>) {
  return (
    <>
      {/* KYC Status */}
      <select
        className="h-9 rounded border px-2"
        value={
          (table.getColumn("kycVerified")?.getFilterValue() as string) ?? ""
        }
        onChange={(e) =>
          table
            .getColumn("kycVerified")
            ?.setFilterValue(e.target.value || undefined)
        }
      >
        <option value="">All KYC Status</option>
        <option value="Success">Success</option>
        <option value="Failed">Failed</option>
        <option value="false">Not Submitted</option>
      </select>

      {/* Email Verified */}
      <select
        className="h-9 rounded border px-2"
        value={(table.getColumn("verified")?.getFilterValue() as string) ?? ""}
        onChange={(e) =>
          table
            .getColumn("verified")
            ?.setFilterValue(e.target.value || undefined)
        }
      >
        <option value="">All Email Verified</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </>
  );
}
